import { Description, InsertDriveFile, MenuBook } from "@mui/icons-material"
import Box from "@mui/material/Box"
import Card from "@mui/material/Card"
import Container from "@mui/material/Container"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import CircularProgress from "@mui/material/CircularProgress"
import Snackbar from "@mui/material/Snackbar"
import Alert from "@mui/material/Alert"
import Menu from "@mui/material/Menu"
import MenuItem from "@mui/material/MenuItem"
import { useState, useEffect } from "react"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { downloadFile } from "../../utils/downloadFile"
import { fetchDocumentUrl } from "@/features/documents/documentsThunks"
import { fetchForms, fetchFormUrl } from "@/features/documents/formsThunks"

// Storage paths for documents in Firebase Storage
const STORAGE_PATHS = {
    manualConvivencia: 'Manual de Convivencia JEC.pdf',
    sistemaEvaluacion: 'SIEE JEC.pdf',
}

const InstitucionalDocuments: React.FC = () => {
    const dispatch = useAppDispatch()
    const forms = useAppSelector((state) => state.forms.forms)
    const [loadingDoc, setLoadingDoc] = useState<string | null>(null)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [showError, setShowError] = useState(false)
    const [formatMenuAnchor, setFormatMenuAnchor] = useState<null | HTMLElement>(null)
    const [formatOptions, setFormatOptions] = useState<{ storagePath: string; name: string; fileName: string; }[]>([])

    // Fetch forms on component mount
    useEffect(() => {
        dispatch(fetchForms())
    }, [dispatch])

    useEffect(() => {
        if (forms.length > 0 && !formatOptions.length) {
            const _formatOptions = forms
                .filter((form) => form.docUrl) // Filter out forms without docUrl
                .map((form) => {
                    // Extract filename from docUrl or use the form name
                    const fileName = form.docUrl.includes('/')
                        ? form.docUrl.split('/').pop() || form.name
                        : form.docUrl

                    return {
                        storagePath: form.docUrl,
                        name: form.name,
                        fileName: fileName,
                    }
                })

            setFormatOptions(_formatOptions)
        }
    }, [forms])



    const handleDownloadDocument = async (storagePath: string, fileName: string) => {
        setLoadingDoc(storagePath)
        setErrorMessage(null)
        setShowError(false)

        try {
            const downloadUrl = await dispatch(fetchDocumentUrl({ storagePath: storagePath })).unwrap()
            await downloadFile(downloadUrl, fileName)
        } catch (err) {
            let userMessage = 'Error al descargar el documento. Por favor, intente nuevamente.'

            if (err instanceof Error) {
                if (err.message.includes('not found') || err.message.includes('does not exist')) {
                    userMessage = 'El documento no está disponible en este momento.'
                } else if (err.message.includes('unauthorized') || err.message.includes('permission')) {
                    userMessage = 'No tiene permisos para acceder a este documento.'
                } else if (err.message.includes('network')) {
                    userMessage = 'Error de conexión. Verifique su conexión a internet.'
                } else {
                    userMessage = err.message
                }
            }
            setErrorMessage(userMessage)
            setShowError(true)
        } finally {
            setLoadingDoc(null)
        }
    }

    const handleFormatClick = (event: React.MouseEvent<HTMLElement>) => {
        setFormatMenuAnchor(event.currentTarget)
    }

    const handleFormatClose = () => {
        setFormatMenuAnchor(null)
    }

    const handleFormatDownload = async (storagePath: string, fileName: string) => {
        handleFormatClose()
        setLoadingDoc(storagePath)
        setErrorMessage(null)
        setShowError(false)

        try {
            // Use fetchFormUrl for forms (stored in 'forms/' root)
            const downloadUrl = await dispatch(fetchFormUrl({ storagePath: storagePath })).unwrap()
            await downloadFile(downloadUrl, fileName)
        } catch (err) {
            let userMessage = 'Error al descargar el documento. Por favor, intente nuevamente.'

            if (err instanceof Error) {
                if (err.message.includes('not found') || err.message.includes('does not exist')) {
                    userMessage = 'El documento no está disponible en este momento.'
                } else if (err.message.includes('unauthorized') || err.message.includes('permission')) {
                    userMessage = 'No tiene permisos para acceder a este documento.'
                } else if (err.message.includes('network')) {
                    userMessage = 'Error de conexión. Verifique su conexión a internet.'
                } else {
                    userMessage = err.message
                }
            }
            setErrorMessage(userMessage)
            setShowError(true)
        } finally {
            setLoadingDoc(null)
        }
    }

    const handleCloseError = () => {
        setShowError(false)
    }

    return (
        <Box sx={{
            backgroundColor: 'white',
            py: { xs: 5, sm: 6, md: 7, lg: 8, xl: 10 },
            px: { xs: 2, sm: 3 }
        }}>
            <Container maxWidth="lg">
                <Box sx={{
                    textAlign: 'center',
                    mb: { xs: 4, sm: 5, md: 6, lg: 7 },
                    px: { xs: 1, sm: 0 }
                }}>
                    <Typography
                        variant="h4"
                        fontWeight={700}
                        gutterBottom
                        sx={{
                            color: 'primary.dark',
                            fontSize: { xs: '1.375rem', sm: '1.5rem', md: '1.75rem', lg: '1.875rem', xl: '2rem' },
                            mb: { xs: 1.5, md: 2 }
                        }}
                    >
                        Documentos Institucionales
                    </Typography>
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{
                            fontSize: { xs: '0.875rem', sm: '0.938rem', md: '1rem', lg: '1.063rem' },
                            px: { xs: 2, sm: 4, md: 0 }
                        }}
                    >
                        Accede a nuestros documentos oficiales y formatos institucionales
                    </Typography>
                </Box>

                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 3, sm: 4, md: 5, lg: 6 }}
                    justifyContent="center"
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    sx={{ px: { xs: 1, sm: 0 } }}
                >
                    {/* Manual de Convivencia */}
                    <Card
                        onClick={() => handleDownloadDocument(STORAGE_PATHS.manualConvivencia, 'Manual_de_Convivencia.pdf')}
                        sx={{
                            backgroundColor: 'secondary.main',
                            color: 'white',
                            borderRadius: { xs: 2, md: 3 },
                            minWidth: { xs: '100%', sm: 180, md: 200, lg: 220 },
                            minHeight: { xs: 160, sm: 180, md: 190, lg: 200 },
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: loadingDoc === STORAGE_PATHS.manualConvivencia ? 'wait' : 'pointer',
                            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                            p: { xs: 2, sm: 2.5, md: 3 },
                            '&:hover': {
                                transform: { xs: 'scale(1.02)', sm: 'translateY(-8px)' },
                                boxShadow: { sm: '0 12px 24px rgba(0,0,0,0.2)' },
                            },
                        }}
                    >
                        {loadingDoc === STORAGE_PATHS.manualConvivencia ? (
                            <CircularProgress sx={{ color: 'white', mb: { xs: 1.5, md: 2 } }} size={60} />
                        ) : (
                            <MenuBook sx={{
                                fontSize: { xs: 50, sm: 56, md: 60, lg: 70 },
                                mb: { xs: 1.5, md: 2 }
                            }} />
                        )}
                        <Typography
                            variant="h6"
                            fontWeight={600}
                            textAlign="center"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.063rem', md: '1.125rem', lg: '1.188rem' }
                            }}
                        >
                            Manual de
                            <br />
                            Convivencia
                        </Typography>
                    </Card>

                    {/* Sistema Institucional de Evaluación */}
                    <Card
                        onClick={() => handleDownloadDocument(STORAGE_PATHS.sistemaEvaluacion, 'Sistema_Institucional_de_Evaluacion.pdf')}
                        sx={{
                            backgroundColor: 'primary.main',
                            color: 'white',
                            borderRadius: { xs: 2, md: 3 },
                            minWidth: { xs: '100%', sm: 180, md: 200, lg: 220 },
                            minHeight: { xs: 160, sm: 180, md: 190, lg: 200 },
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: loadingDoc === STORAGE_PATHS.sistemaEvaluacion ? 'wait' : 'pointer',
                            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                            p: { xs: 2, sm: 2.5, md: 3 },
                            '&:hover': {
                                transform: { xs: 'scale(1.02)', sm: 'translateY(-8px)' },
                                boxShadow: { sm: '0 12px 24px rgba(0,0,0,0.2)' },
                            },
                        }}
                    >
                        {loadingDoc === STORAGE_PATHS.sistemaEvaluacion ? (
                            <CircularProgress sx={{ color: 'white', mb: { xs: 1.5, md: 2 } }} size={60} />
                        ) : (
                            <Description sx={{
                                fontSize: { xs: 50, sm: 56, md: 60, lg: 70 },
                                mb: { xs: 1.5, md: 2 }
                            }} />
                        )}
                        <Typography
                            variant="h6"
                            fontWeight={600}
                            textAlign="center"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.063rem', md: '1.125rem', lg: '1.188rem' },
                                lineHeight: 1.3
                            }}
                        >
                            Sistema
                            <br />
                            Institucional
                            <br />
                            de Evaluación
                        </Typography>
                    </Card>

                    {/* Formatos */}
                    <Card
                        onClick={handleFormatClick}
                        sx={{
                            backgroundColor: 'primary.dark',
                            color: 'white',
                            borderRadius: { xs: 2, md: 3 },
                            minWidth: { xs: '100%', sm: 180, md: 200, lg: 220 },
                            minHeight: { xs: 160, sm: 180, md: 190, lg: 200 },
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                            p: { xs: 2, sm: 2.5, md: 3 },
                            '&:hover': {
                                transform: { xs: 'scale(1.02)', sm: 'translateY(-8px)' },
                                boxShadow: { sm: '0 12px 24px rgba(0,0,0,0.2)' },
                            },
                        }}
                    >
                        <InsertDriveFile sx={{
                            fontSize: { xs: 50, sm: 56, md: 60, lg: 70 },
                            mb: { xs: 1.5, md: 2 }
                        }} />
                        <Typography
                            variant="h6"
                            fontWeight={600}
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.063rem', md: '1.125rem', lg: '1.188rem' }
                            }}
                        >
                            Formatos
                        </Typography>
                    </Card>
                </Stack>
            </Container>

            {/* Formatos Menu */}
            <Menu
                anchorEl={formatMenuAnchor}
                open={Boolean(formatMenuAnchor)}
                onClose={handleFormatClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                PaperProps={{
                    sx: {
                        mt: 1,
                        minWidth: { xs: 200, sm: 220 },
                        borderRadius: 2,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                    }
                }}
            >
                {formatOptions.map((format) => (
                    <MenuItem
                        key={format.storagePath}
                        onClick={() => handleFormatDownload(format.storagePath, format.fileName)}
                        disabled={loadingDoc === format.storagePath}
                        sx={{
                            py: 1.5,
                            px: 2.5,
                            fontSize: { xs: '0.875rem', sm: '0.938rem' },
                            '&:hover': {
                                backgroundColor: 'primary.light',
                                color: 'primary.contrastText',
                            },
                        }}
                    >
                        {loadingDoc === format.storagePath ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <CircularProgress size={16} />
                                <span>{format.name}</span>
                            </Box>
                        ) : (
                            format.name
                        )}
                    </MenuItem>
                ))}
            </Menu>

            {/* Error Snackbar */}
            <Snackbar
                open={showError}
                autoHideDuration={6000}
                onClose={handleCloseError}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
                    {errorMessage}
                </Alert>
            </Snackbar>
        </Box>
    )
}

export default InstitucionalDocuments